import { useCallback, useEffect, useMemo, useState } from "react";

import {
    AbfLoanExpanded,
    getZcLedger,
    getZcCollateral,
    useLoanWithdrawTransaction,
    useLoopscaleWalletContext,
    WithdrawCollateralTransactionParams,
    getVaultLpStakeUnstakeIxParams,
    isVaultLpCollateral
} from "@bridgesplit/abf-react";
import { decimalsToBps, LOADING_ERROR, Result, uiAmountToLamports } from "@bridgesplit/utils";
import { convertLoanValueTupleToBps } from "@bridgesplit/abf-sdk";
import { TrackTransactionEvent } from "app/types";
import { useTransactionSender } from "app/components/transactions";

import { useCollateralWithMaxWithdrawableAmounts } from "./utils";
import { UseWithdrawCollateralReturn } from "./types";
import { calculateNewLoanHealth } from "../../util";

export function useWithdrawCollateral({
    loanExpanded,
    additionalRepayAmount,
    transactionOptions
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    additionalRepayAmount?: number;
    transactionOptions?: { preventAutoDialogClose?: boolean };
}): UseWithdrawCollateralReturn {
    const [inputAmount, setInputAmount] = useState<number | undefined>(undefined);
    const [selectedCollateralMint, setSelectedCollateralMint] = useState<string | undefined>(undefined);
    const supportedCollateralTokens = useMemo(
        () => loanExpanded?.collateral.map((c) => c.metadata),
        [loanExpanded?.collateral]
    );

    useEffect(() => {
        if (!supportedCollateralTokens?.length) return;
        const selectedIsSupported = supportedCollateralTokens.some((token) => token.mint === selectedCollateralMint);
        if (!selectedIsSupported) setSelectedCollateralMint(supportedCollateralTokens[0].mint);
    }, [selectedCollateralMint, supportedCollateralTokens]);

    const collateral = useMemo(
        () =>
            loanExpanded?.collateral.find((c) => c.metadata.mint === selectedCollateralMint) ??
            getZcCollateral(loanExpanded),
        [loanExpanded, selectedCollateralMint]
    );
    const collateralMetadata = collateral?.metadata;

    const { collateralWithMaxWithdrawableAmounts, minRepayAmount } = useCollateralWithMaxWithdrawableAmounts(
        loanExpanded,
        additionalRepayAmount
    );
    const maxWithdrawable =
        collateralWithMaxWithdrawableAmounts?.find(
            (c) => c.metadata.mint === collateral?.metadata.mint || c.key === collateral?.metadata.mint
        )?.amount ?? 0;

    const send = useTransactionSender();
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();

    const healthChange = useMemo(
        () => calculateNewLoanHealth(loanExpanded, -(inputAmount ?? 0), collateral?.usdPrice ?? 0),
        [loanExpanded, inputAmount, collateral?.usdPrice]
    );

    const creditBookWithdraw = useLoanWithdrawTransaction();

    const submit = useCallback(async () => {
        const ledgerAccount = getZcLedger(loanExpanded);
        if (!loanExpanded || !activeWallet) return Result.errFromMessage(LOADING_ERROR);
        if (!inputAmount) return Result.errFromMessage("Enter an amount");
        if (!collateral) return Result.errFromMessage("No collateral found");
        const collateralAmount = uiAmountToLamports(inputAmount, collateral.metadata.decimals);

        const params: WithdrawCollateralTransactionParams = {
            loan: loanExpanded.loan.address,
            collateralMint: collateral.metadata.mint,
            amount: collateralAmount,
            expectedLoanValues: {
                expectedApy: decimalsToBps(ledgerAccount?.apy ?? 0),
                expectedLqt: convertLoanValueTupleToBps(ledgerAccount?.lqtRatios ?? [0, 0, 0, 0, 0])
            },
            collateralIndex: collateral.index,
            stakeInstructions: getVaultLpStakeUnstakeIxParams({
                vault: collateral?.oracleAccount,
                amountLp: collateralAmount,
                skip: !isVaultLpCollateral(collateral.metadata) || !collateralAmount
            })
        };

        return await send(creditBookWithdraw, params, {
            description: "Withdrawing collateral",
            sendOptions: {
                onSuccess: () => {
                    setInputAmount(undefined); // Clear input after success
                }
            },
            mixpanelEvent: { key: TrackTransactionEvent.CollateralWithdraw, params },
            ...transactionOptions
        });
    }, [activeWallet, loanExpanded, inputAmount, collateral, creditBookWithdraw, send, transactionOptions]);

    const ctaError = useMemo(() => {
        if (!inputAmount) return "Enter an amount";
        if (!maxWithdrawable) return "No collateral available";
        if (inputAmount > maxWithdrawable) return "Exceeds available";
        return null;
    }, [inputAmount, maxWithdrawable]);

    return {
        inputAmount,
        setInputAmount,
        maxWithdrawable,
        collateralMetadata,
        supportedCollateralTokens,
        setCollateralToken: (token) => {
            setSelectedCollateralMint(token.mint);
            setInputAmount(undefined);
        },
        submit,
        ctaError,
        minRepayAmount,
        healthChange
    };
}
